import React, { useEffect, useRef, useState } from 'react'
import { Steps } from 'intro.js-react'
import { Avatar, Box, Button, Container, Grid, Paper, Stack, Typography } from '@common/components'
import { FEEDBACK_ROUTE, PERSONAL_ROUTE } from '@common/constants/routes'
import { Outlet, RouterLink, useLocation } from '@lib/router'
import { SidebarDashboard } from '../sidebar-dashboard'
import 'intro.js/introjs.css'
import { useUser } from '@features/auth/hooks'
import { DisplayClassroom } from '@features/classrooms/components'
import NextSessionTime from '@features/event/components/next-session-time'
import { useTheme } from '@features/theme/theme.hooks'
import { Theme, useMediaQuery } from '@mui/material'
import { useAuthWhoamiQuery } from '@app/services/auto-generated/auth'
import { useModulesSubmitModuleResultMutation } from '@app/services/auto-generated/modules'
import { handleGravatar } from '@common/utils/gravatar'
import { isUNSW } from '@utils'

const DashboardLayout = () => {
  const location = useLocation()
  const { user, isTeacher } = useUser()
  const { refetch } = useAuthWhoamiQuery()
  const [sendModule] = useModulesSubmitModuleResultMutation()
  const { isDark } = useTheme()
  const [steps, setSteps] = useState<{ element: string; intro: string }[]>([])
  const contentRef = useRef<HTMLDivElement>(null)
  const isSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const stepsTeacher = [
    {
      element: '#speaking-tasks',
      intro: "These are your students' speaking tasks, similar to homework assignments.",
    },
    {
      element: '#add-new-task',
      intro: 'You can create a new task here.',
    },
    {
      element: '#my-tasks-button',
      intro: 'You can view all your tasks here.',
    },
    {
      element: '#ispeak-tasks-button',
      intro: 'You can view all iSpeak tasks here. These tasks are created by our team.',
    },
    {
      element: '#live-practice-button',
      intro: 'You can view all live practice sessions here.',
    },
  ]
  const stepsStudent = [
    {
      element: '#main-tasks-button',
      intro: 'You can view the tasks assigned to you here.',
    },
    {
      element: '#student-record-box',
      intro: 'You can record your voice here while practicing English.',
    },
    {
      element: '#record-button',
      intro: 'Click here to start recording.',
    },
    {
      element: '#completed-recordings-bar',
      intro: 'After recording, you can find your recordings and feedback here.',
    },
    {
      element: '#summary-tab-button',
      intro: 'You can view a comprehensive feedback summary here.',
    },
    {
      element: '#feedback-tab-button',
      intro: 'You can view detailed feedback here.',
    },
  ]

  useEffect(() => {
    if (isTeacher) {
      setSteps(stepsTeacher)
    } else if (!isTeacher) {
      setSteps(stepsStudent)
    }
  }, [isTeacher])

  const initialStep = 0
  const [stepsEnabled, setStepsEnabled] = React.useState(false)

  useEffect(() => {
    if (steps.length === 0) {
      return
    }
    // actually this is retarded, but it works
    const observer = new MutationObserver(function () {
      const everythingRendered = steps.every((step) => {
        return document.querySelector(step.element)
      })

      if (everythingRendered) {
        if (user?.completed_onboarding === false) {
          setStepsEnabled(true)
        }
        observer.disconnect()
      }
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })
  }, [steps])

  useEffect(() => {
    if (user?.completed_onboarding === true) {
      setStepsEnabled(false)
    }
  }, [user])

  useEffect(() => {
    if (isDark) {
      import('intro.js/themes/introjs-dark.css')
    }
  }, [isDark])

  const completeOnboardingTour = async (stepIndex: number) => {
    await sendModule({
      moduleResult: {
        id: 'OnboardingTourModule',
        data: {
          finished_all_steps: stepIndex === stepsTeacher.length - 1,
        },
      },
    }).unwrap()
    refetch()
  }

  useEffect(() => {
    return () => {
      const helperLayer = document.querySelector('.introjs-helperLayer')
      if (helperLayer) {
        helperLayer.remove()
      }
    }
  }, [stepsEnabled])

  const handleScrollToContent = () => {
    if (isSmDown) {
      contentRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <>
      <Container maxWidth={'xl'} sx={{ mb: 6, mx: 'auto', mt: 1 }}>
        {!isUNSW() && stepsEnabled && (
          <Steps
            enabled={stepsEnabled}
            steps={steps}
            initialStep={initialStep}
            onExit={completeOnboardingTour}
            options={{
              showStepNumbers: true,
            }}
            onBeforeExit={() => {
              setStepsEnabled(false)
            }}
          />
        )}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ display: { xs: 'flex', md: 'none' }, mb: 2 }}
        >
          <Paper
            sx={{
              padding: '15px',
              width: { xs: '100%', sm: '50%' },
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              borderRadius: '10px',
              height: { xs: '70px', sm: 'auto' },
              justifyContent: 'flex-start',
            }}
          >
            <Avatar
              sx={{
                '.MuiAvatar-root': {
                  color: 'black',
                },
              }}
              src={handleGravatar(user?.image ?? '')}
            />
            <Stack>
              <Typography sx={{ ml: 1 }}>
                Welcome back{' '}
                <Box
                  component='span'
                  sx={{
                    fontWeight: 600,
                    maxWidth: { xs: '250px', sm: '170px' },
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'inline-block', // Make sure it stays inline
                    verticalAlign: 'bottom', // Align properly with the text
                  }}
                >
                  {user?.full_name ?? user?.username}
                </Box>
              </Typography>
            </Stack>
            <Button
              component={RouterLink}
              to={PERSONAL_ROUTE}
              variant='outlined'
              color='neutral'
              sx={{ borderRadius: '20px', ml: 'auto' }}
            >
              My Profile
            </Button>
          </Paper>
          {!isUNSW() ? <NextSessionTime /> : <DisplayClassroom />}
        </Stack>
        <Grid container spacing={4} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
          <Grid item xs={12} md={3.5}>
            <SidebarDashboard onItemClick={handleScrollToContent} />
          </Grid>
          <Grid item xs={12} md={8.5}>
            <Paper
              sx={{
                borderRadius: '20px',
                height: 'calc(100vh - 150px)',
                minHeight: { xs: '630px', md: 'calc(100vh - 150px)' },
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              <Box
                ref={contentRef}
                sx={{
                  flex: 1,
                  overflowY: location.pathname.startsWith(FEEDBACK_ROUTE) ? 'hidden' : 'auto',
                }}
              >
                <Box sx={{ height: '100%' }}>
                  <Outlet />
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  )
}

export default DashboardLayout
